// src/features/tidsregistering/components/attendance/CreatePlannedShiftDialog.jsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stack,
  Alert,
  Divider,
  TextField,
} from '@mui/material';
import { format } from 'date-fns';
import StaffMember from '../../../../components/common/StaffMember';
import { unavailabilityAPI } from '../../services/unavailability';

/**
 * Dialog for manually creating a planned shift on the schedule track.
 *
 * Props:
 *   open              — boolean
 *   onClose           — () => void
 *   onSubmit          — ({ staff_member_id, date, start_time, end_time, break_minutes }) => void
 *   staffMember       — StaffMember object (pre-filled, read-only)
 *   selectedDate      — string 'yyyy-MM-dd'
 *   initialStartTime  — Date  pre-filled from drag
 *   initialEndTime    — Date  pre-filled from drag
 *   loading           — boolean
 *   submitError       — string | null
 */
export default function CreatePlannedShiftDialog({
  open,
  onClose,
  onSubmit,
  staffMember,
  selectedDate,
  initialStartTime,
  initialEndTime,
  loading = false,
  submitError = null,
}) {
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [breakMinutes, setBreakMinutes] = useState(0);
  const [availabilityWarning, setAvailabilityWarning] = useState(false);

  useEffect(() => {
    if (open) {
      setStartTime(initialStartTime ? format(initialStartTime, 'HH:mm') : '');
      setEndTime(initialEndTime ? format(initialEndTime, 'HH:mm') : '');
      setBreakMinutes(0);
      setAvailabilityWarning(false);
    }
  }, [open, initialStartTime, initialEndTime]);

  useEffect(() => {
    if (!open || !staffMember || !startTime || !endTime) {
      setAvailabilityWarning(false);
      return;
    }
    let cancelled = false;
    unavailabilityAPI
      .checkStaffAvailability(staffMember.staff_member_id, selectedDate, selectedDate, startTime, endTime)
      .then((res) => {
        if (!cancelled) setAvailabilityWarning(res.data?.is_available === false);
      })
      .catch(() => {
        if (!cancelled) setAvailabilityWarning(false);
      });
    return () => { cancelled = true; };
  }, [open, staffMember?.staff_member_id, selectedDate, startTime, endTime]); // eslint-disable-line react-hooks/exhaustive-deps

  const canSubmit = !!startTime && !!endTime && !loading;

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit({
      staff_member_id: staffMember.staff_member_id,
      date: selectedDate,
      start_time: startTime,
      end_time: endTime,
      break_minutes: breakMinutes,
      is_working: true,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Opret vagtplan</DialogTitle>

      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          {staffMember && (
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                Medarbejder
              </Typography>
              <StaffMember staffMember={staffMember} avatarSize={32} clickable={false} />
            </Box>
          )}

          <Divider />

          <Typography variant="caption" color="text.secondary">
            {selectedDate}
          </Typography>

          <Stack direction="row" spacing={2}>
            <TextField
              label="Fra"
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              label="Til"
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Stack>

          <TextField
            label="Pause (minutter)"
            type="number"
            value={breakMinutes}
            onChange={(e) => setBreakMinutes(Math.max(0, parseInt(e.target.value, 10) || 0))}
            fullWidth
            slotProps={{ htmlInput: { min: 0, step: 5 } }}
          />

          {availabilityWarning && (
            <Alert severity="warning">
              Medarbejderen har godkendt fravær eller aktivitet i dette tidsrum.
            </Alert>
          )}

          {submitError && (
            <Alert severity="error" sx={{ whiteSpace: 'pre-line' }}>
              {submitError}
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Annuller
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit}>
          {loading ? 'Opretter…' : 'Opret'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
